import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Star,
  RefreshCw,
  Loader2,
  LinkIcon,
  Unlink,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const API = `${window.location.origin}/api`;

function StatusBadge({ connected }) {
  if (connected) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        <CheckCircle2 className="w-3 h-3" /> Connesso
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
      <AlertCircle className="w-3 h-3" /> Non connesso
    </span>
  );
}

export default function GoogleBusinessPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/google-business/status`, {
        withCredentials: true,
      });
      setStatus(data);
    } catch (err) {
      console.debug("gbusiness status:", err?.response?.status);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Detect the redirect from OAuth callback (?gbusiness=connected|error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("gbusiness");
    if (!flag) return;
    if (flag === "connected") {
      toast.success("Google Business collegato!");
    } else if (flag === "error") {
      const msg = params.get("msg") || "errore sconosciuto";
      toast.error(`Errore connessione Google Business: ${msg}`);
    }
    // clean the URL
    params.delete("gbusiness");
    params.delete("msg");
    const cleaned = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
    window.history.replaceState({}, "", cleaned);
    refresh();
  }, [refresh]);

  const handleConnect = async () => {
    try {
      const { data } = await axios.get(`${API}/google-business/oauth/start`, {
        withCredentials: true,
      });
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error("URL di autorizzazione non disponibile");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore avvio OAuth");
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Sei sicuro di voler scollegare Google Business?")) return;
    try {
      await axios.post(`${API}/google-business/disconnect`, {}, {
        withCredentials: true,
      });
      toast.success("Google Business scollegato");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const { data } = await axios.get(`${API}/google-business/accounts`, {
        withCredentials: true,
      });
      setAccounts(data?.accounts || []);
      if ((data?.accounts || []).length === 0) {
        toast.warning("Nessun account Business Profile trovato per questo utente");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore listing accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadLocations = async (accountName) => {
    setSelectedAccount(accountName);
    setLoadingLocations(true);
    try {
      const { data } = await axios.get(`${API}/google-business/locations`, {
        params: { account_name: accountName },
        withCredentials: true,
      });
      setLocations(data?.locations || []);
      if ((data?.locations || []).length === 0) {
        toast.warning("Nessuna location trovata in questo account");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore listing locations");
    } finally {
      setLoadingLocations(false);
    }
  };

  const selectLocation = async (loc) => {
    try {
      await axios.post(
        `${API}/google-business/select-location`,
        {
          account_name: selectedAccount,
          location_name: loc.name,
          location_title: loc.title || "",
        },
        { withCredentials: true },
      );
      toast.success(`Location selezionata: ${loc.title || loc.name}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio location");
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/google-business/sync`, {}, {
        withCredentials: true,
      });
      toast.success(
        `Sync completato: ${data.fetched} recensioni (${data.inserted} nuove, ${data.updated} aggiornate)`,
      );
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore sync recensioni");
    } finally {
      setSyncing(false);
    }
  };

  // DRY RUN diagnostico: fetcha da Google ma NON salva. Mostra al pannello
  // admin il breakdown (con/senza commento, per stelle) della risposta
  // reale di Google. Utile per diagnosticare il bug "le star-only non
  // vengono conteggiate" — capiamo se la perdita è a livello API Google
  // o nella nostra logica di save.
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const runDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const { data } = await axios.get(`${API}/google-business/diagnostics`, {
        withCredentials: true,
      });
      setDiagnostics(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore diagnostics");
    } finally {
      setDiagLoading(false);
    }
  };

  return (
    <div
      className="bg-white rounded-3xl p-4 sm:p-8 shadow-md space-y-4"
      data-testid="google-business-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0">
            <Star className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
              Recensioni Google <StatusBadge connected={!!status?.connected} />
            </h2>
            <p className="text-xs text-slate-500">
              Sincronizza le recensioni della scheda Google Business di BDD Studio Dentistico.
            </p>
          </div>
        </div>
        {!loading && (
          status?.connected ? (
            <Button
              variant="outline"
              onClick={handleDisconnect}
              className="rounded-xl text-rose-600 border-rose-200 hover:bg-rose-50"
              data-testid="gbusiness-disconnect-btn"
            >
              <Unlink className="w-4 h-4 mr-2" /> Scollega
            </Button>
          ) : (
            <Button
              onClick={handleConnect}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl"
              data-testid="gbusiness-connect-btn"
            >
              <LinkIcon className="w-4 h-4 mr-2" /> Collega Google Business
            </Button>
          )
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Caricamento…
        </div>
      ) : status?.connected ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-3">
              <div className="text-slate-400 uppercase text-[10px] font-bold mb-1">Account</div>
              <div className="text-slate-800 font-semibold truncate">
                {status.account_email || "—"}
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-3">
              <div className="text-slate-400 uppercase text-[10px] font-bold mb-1">Location</div>
              <div className="text-slate-800 font-semibold truncate">
                {status.location_title || (status.location_name ? status.location_name : "Non selezionata")}
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-3">
              <div className="text-slate-400 uppercase text-[10px] font-bold mb-1">Recensioni cached</div>
              <div className="text-slate-800 font-semibold">{status.reviews_count}</div>
              {/* Breakdown con/senza commento + per stelle. Aiuta a
                  capire se la mancanza delle star-only è già nel DB
                  locale (= problema fetch da Google). */}
              {(status.reviews_with_comment !== undefined) && (
                <div className="text-[10px] text-slate-500 mt-1 space-y-0.5">
                  <div>
                    💬 Con commento: <span className="font-semibold">{status.reviews_with_comment}</span>
                  </div>
                  <div>
                    ⭐ Solo stelle: <span className="font-semibold">{status.reviews_star_only}</span>
                  </div>
                  {status.reviews_by_star && Object.keys(status.reviews_by_star).length > 0 && (
                    <div className="pt-1 border-t border-slate-200 mt-1">
                      {Object.entries(status.reviews_by_star)
                        .sort((a, b) => Number(b[0]) - Number(a[0]))
                        .map(([star, n]) => (
                          <span key={star} className="inline-block mr-1.5">{star}★: {n}</span>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {status.last_sync_at && (
            <div className="text-[11px] text-slate-400">
              Ultimo sync: {new Date(status.last_sync_at).toLocaleString("it-IT")} · {status.last_sync_count}{" "}
              recensioni recuperate
            </div>
          )}

          {!status.location_name && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
              <div className="text-xs text-amber-800">
                <strong>Step 1:</strong> Seleziona l'account Business Profile e poi la location dello studio.
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={loadAccounts}
                disabled={loadingAccounts}
                className="rounded-lg text-xs"
                data-testid="gbusiness-load-accounts-btn"
              >
                {loadingAccounts ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ChevronRight className="w-3 h-3 mr-1" />}
                Carica account
              </Button>
            </div>
          )}

          {accounts.length > 0 && !status.location_name && (
            <div className="space-y-1 text-xs">
              <div className="text-slate-500 font-bold uppercase text-[10px]">Account</div>
              {accounts.map((a) => (
                <button
                  key={a.name}
                  onClick={() => loadLocations(a.name)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs flex items-center justify-between transition-colors ${
                    selectedAccount === a.name
                      ? "border-indigo-300 bg-indigo-50 text-indigo-800"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`gbusiness-account-${a.name}`}
                >
                  <span className="truncate">
                    <strong>{a.accountName || a.name}</strong>
                    <span className="text-slate-400 ml-1">{a.type} · {a.role}</span>
                  </span>
                  <ChevronRight className="w-3 h-3 text-slate-400" />
                </button>
              ))}
            </div>
          )}

          {selectedAccount && (
            <div className="space-y-1 text-xs">
              <div className="text-slate-500 font-bold uppercase text-[10px]">Location</div>
              {loadingLocations ? (
                <div className="text-slate-400 italic flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Caricamento…
                </div>
              ) : locations.length === 0 ? (
                <div className="text-slate-400 italic">Nessuna location</div>
              ) : (
                locations.map((loc) => (
                  <button
                    key={loc.name}
                    onClick={() => selectLocation(loc)}
                    className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-xs flex items-center justify-between transition-colors"
                    data-testid={`gbusiness-location-${loc.name}`}
                  >
                    <span className="truncate">
                      <strong>{loc.title || loc.name}</strong>
                      {loc.storeCode && <span className="text-slate-400 ml-1">{loc.storeCode}</span>}
                    </span>
                    <ChevronRight className="w-3 h-3 text-slate-400" />
                  </button>
                ))
              )}
            </div>
          )}

          {status.location_name && (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={triggerSync}
                disabled={syncing}
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl"
                data-testid="gbusiness-sync-btn"
              >
                {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                {syncing ? "Sincronizzazione…" : "Sincronizza recensioni"}
              </Button>
              <Button
                onClick={runDiagnostics}
                disabled={diagLoading}
                variant="outline"
                className="rounded-xl text-xs"
                data-testid="gbusiness-diagnostics-btn"
                title="DRY RUN: fetcha da Google senza salvare. Mostra cosa risponde realmente Google."
              >
                {diagLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertCircle className="w-4 h-4 mr-2" />}
                Diagnostica fetch
              </Button>
            </div>
          )}

          {diagnostics && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs space-y-2" data-testid="gbusiness-diagnostics-panel">
              <div className="font-semibold text-blue-900">Risultato dry-run fetch Google</div>
              <div className="grid grid-cols-2 gap-2 text-blue-900">
                <div>Totale ritornato da Google:</div>
                <div className="font-bold">{diagnostics.google_returned_total}</div>
                <div>Con commento:</div>
                <div className="font-bold">{diagnostics.with_comment}</div>
                <div>Solo stelle (nella risposta):</div>
                <div className="font-bold">{diagnostics.star_only_in_response}</div>
                {diagnostics.missing_name_field > 0 && (
                  <>
                    <div>Senza campo `name` (scartate):</div>
                    <div className="font-bold text-red-700">{diagnostics.missing_name_field}</div>
                  </>
                )}
                {diagnostics.missing_or_invalid_star_rating > 0 && (
                  <>
                    <div>Senza `starRating`:</div>
                    <div className="font-bold text-amber-700">{diagnostics.missing_or_invalid_star_rating}</div>
                  </>
                )}
              </div>
              {diagnostics.by_star_rating && Object.keys(diagnostics.by_star_rating).length > 0 && (
                <div className="text-blue-800 text-[11px]">
                  Per stelle: {Object.entries(diagnostics.by_star_rating).map(([k, v]) => `${k}=${v}`).join(", ")}
                </div>
              )}
              <div className="bg-white border border-blue-100 rounded-lg p-2 text-blue-900">
                {diagnostics.interpretation}
              </div>
              {(diagnostics.sample_star_only || []).length > 0 && (
                <details className="text-blue-800">
                  <summary className="cursor-pointer">Sample raw payload review star-only</summary>
                  <pre className="text-[10px] mt-1 overflow-x-auto bg-white p-2 rounded">
                    {JSON.stringify(diagnostics.sample_star_only[0], null, 2)}
                  </pre>
                </details>
              )}
              {(diagnostics.sample_with_comment || []).length > 0 && (
                <details className="text-blue-800">
                  <summary className="cursor-pointer">Sample raw payload review con commento</summary>
                  <pre className="text-[10px] mt-1 overflow-x-auto bg-white p-2 rounded">
                    {JSON.stringify(diagnostics.sample_with_comment[0], null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-slate-500 space-y-2">
          <p>
            La connessione Google Business sblocca il grafico mensile delle recensioni nella pagina Feedback.
          </p>
          <p className="text-amber-700">
            ⚠️ <strong>Importante:</strong> l'accesso alle API delle recensioni richiede l'approvazione di Google.{" "}
            <a
              href="https://support.google.com/business/contact/api_default"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-amber-900"
            >
              Compila questo modulo
            </a>{" "}
            con l'account proprietario della scheda. Fino all'approvazione il sync recensioni darà errore 403.
          </p>
        </div>
      )}
    </div>
  );
}
